// ** React Imports
import { useEffect, useState, useContext, useRef, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";

// ** Redux
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import Helmet from "react-helmet";

// ** Custom Components
import Loader from "components/layout/Loader";
import LoadingContainer from "components/layout/LoadingContainer";
import ScrollToTop from "components/global/ScrollToTop";
import { CardWrapper } from "components/layout/Card/CardWrapper";
import { CardTitle } from "components/layout/Card/CardTitle";
import { CardBody } from "components/layout/Card/CardBody";
import Heading from "components/typography/Heading";
import { CardsBlockWrapper } from "components/layout/Card/CardsBlockWrapper";
import EmptyBlock from "components/layout/Block/BlockEmpty";

// ** Activity Components
import NotificationsPresenter from "./components/NotificationsPresenter";
import Filters from "./components/Filters";
import StyledLoaderWrapper from "./components/StyledLoaderWrapper";

// ** Custom Hooks
import useProcess from "hooks/useProcess";
import useOpenGraphMetaTags from "hooks/useOpenGraphMetaTags";

// ** Utils & Helpers
import api from "helpers/ApiClient";
import { ANY } from "helpers/constants";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const Activity = ({
	route: { path },
	match: {
		params: { date = ANY },
	},
}) => {
	const { t } = useTranslation(["pages"]);
	const location = useLocation();

	const isAuth = useSelector((state) => state.globalCommon.isAuth);

	const { isPhone } = useContext(deviceTypeContext);
	const history = useHistory();
	const title = t(`pages:activity`);
	const currentPage = Number(1);

	const [type, setType] = useState(null);
	const [notificationData, setNotificationData] = useState([]);
	const [isChangingFilter, setisChangingFilter] = useState(false);

	const {
		loaded,
		loading,
		data: notifications,
		process,
	} = useProcess({
		initial: {
			notifications: [],
			type: null,
			userType: null,
			params: {
				type: null,
				userType: null,
			},
			total: 0,
			pagination: {
				currentpage: 0,
				totalpages: 0,
			},
		},
		transform: (initData, newData) => ({ ...initData, ...newData }),
		promise: (params) => {
			const url = `/notification/activity`;
			return api.get(url, {
				params,
				headers: {
					Accept: "application/api.1.2+json",
				},
			});
		},
	});

	const triggerObserver = useRef();
	const triggerRef = useCallback(
		(node) => {
			if (loading || !("IntersectionObserver" in window)) return;
			if (triggerObserver.current) triggerObserver.current.disconnect();

			triggerObserver.current = new IntersectionObserver(
				(entries) => {
					const isIntersecting = entries.some(
						(entry) => entry.isIntersecting
					);
					if (
						isIntersecting &&
						notificationData?.pagination?.currentpage &&
						notificationData?.pagination?.pagination[2]
					) {
						process({
							page: notificationData?.pagination?.currentpage + 1,
							date,
							type,
						}).then((res) => {
							setNotificationData((prev) => {
								const newData = [
									...prev.notifications,
									...(res.notifications || []),
								];
								return {
									params: res.params,
									total: res.total,
									pagination: res.pagination,
									notifications: newData.filter((val, id) => {
										return (
											id ===
											newData.findIndex(
												(el) => el.id === val.id
											)
										);
									}),
								};
							});
						});
					}
				},
				{ rootMargin: `0px 0px 50px 0px` }
			);
			if (node) triggerObserver.current.observe(node);
		},
		[loading, notificationData]
	);

	useEffect(() => {
		if (location.state?.forceReload) return;
		setisChangingFilter(true);
		process({
			page: currentPage,
			date,
			type,
		})
			.then((res) => {
				setNotificationData(() => {
					return {
						params: res.params,
						total: res.total,
						pagination: res.pagination,
						notifications: [...(res.notifications || [])],
					};
				});
			})
			.finally(() => {
				setisChangingFilter(false);
			});
	}, [date, type, location.pathname, isPhone]);

	useEffect(() => {
		if (location.state?.forceReload) {
			setisChangingFilter(true);
			process({
				page: currentPage,
				date,
				type,
			})
				.then((res) => {
					setNotificationData(() => {
						return {
							params: res.params,
							total: res.total,
							pagination: res.pagination,
							notifications: [...(res.notifications || [])],
						};
					});
				})
				.finally(() => {
					setisChangingFilter(false);
				});
			history.replace({ ...location, state: null });
		}
	}, [location.key, location.state?.forceReload]);

	useEffect(() => {
		// ** Sprawdzenie czy zalogowany
		// ** jeżeli nie przekierowujemy do strony głównej
		if (!isAuth) {
			history.replace("/");
			window.scrollTo(0, 0);
		}
	}, [isAuth]);

	// ** Open Graph Meta Tags
	const ogMetaTags = useOpenGraphMetaTags();

	// #####################################################

	return (
		<>
			{/* Sekcja HEAD */}
			<Helmet title={title}>{ogMetaTags}</Helmet>

			<ScrollToTop />

			{/* Główna zawartość strony */}
			<CardsBlockWrapper>
				<CardWrapper>
					<CardTitle>
						<Heading type="h2">{title}</Heading>
					</CardTitle>
					<CardBody>
						{loaded && (
							<Filters
								date={date}
								type={notifications.params.type}
								setType={setType}
								path={path}
							/>
						)}
					</CardBody>
				</CardWrapper>
				{!loading &&
					loaded &&
					notifications.notifications.length === 0 && (
						<CardWrapper>
							<CardBody>
								<LoadingContainer
									empty
									noContentOnCriteriaText={t(
										"pages:activityEmpty"
									)}
									noContentText={t("pages:activityEmpty")}
									loaderDefault
								/>
							</CardBody>
						</CardWrapper>
					)}
				<>
					{!isChangingFilter && (
						<NotificationsPresenter
							data={notificationData.notifications}
						/>
					)}
					{(loading || isChangingFilter) && (
						<CardWrapper>
							<CardBody>
								<StyledLoaderWrapper noMargin>
									<Loader />
								</StyledLoaderWrapper>
							</CardBody>
						</CardWrapper>
					)}
					{!loading &&
						!isChangingFilter &&
						!(
							notificationData?.pagination?.currentpage > 0 &&
							notifications?.notifications?.length === 0
						) && (
							<div
								ref={triggerRef}
								style={{
									height: "1px",
									pointerEvents: "none",
								}}
							/>
						)}
				</>
				<EmptyBlock />
			</CardsBlockWrapper>
		</>
	);
};

// #####################################################

export default Activity;

// #####################################################
